/**
 * homework.top_attention — top pending attention items for the workspace.
 * Mirrors what's in the dashboard's narration card but as a focused widget
 * users can pin to a custom layout.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

export function TopAttention({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 5;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "homework.top_attention", workspaceId, limit],
    queryFn: async () => {
      // Reuse the session-kickoff endpoint's top_attention for now;
      // a dedicated endpoint can ship later if needed.
      const res = await fetch("/api/v1/session-kickoff", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      const json = await res.json();
      const items = (json.top_attention ?? []) as Array<any>;
      return items.filter((it) => it.workspace_id === workspaceId).slice(0, limit);
    },
  });
  if (error) return <DataUnavailable message="Attention items could not be loaded." retry={refetch} />;
  if (isLoading) {
    return (
      <div role="status" className="space-y-2">
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  if (!data || data.length === 0) {
    return <div className="text-stone-500 text-xs">No pending attention items.</div>;
  }
  return (
    <ul className="space-y-2 text-xs">
      {data.map((it: any) => (
        <li key={it.id} className="flex items-start gap-2 pb-2 border-b border-stone-100 last:border-b-0">
          {it.is_assigned_to_you && (
            <span className="tier-tag bg-amber-100 text-amber-800 mt-0.5">YOU</span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-stone-700">{it.summary ?? `(${String(it.id).slice(0, 8)})`}</div>
            {it.why_user && <div className="text-stone-500 text-[10px] mt-0.5">{it.why_user}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}
